/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/
import React, { useCallback, useEffect, useState } from "react";
import { SxProps } from "@mui/material";
import { Button } from "../../ui/button";
import { Icon } from "../Icon";
import CustomDataGrid from "../Table/CustomDataGrid";
import { GridColDef, GridEventListener, GridRowId, GridSortModel } from "@mui/x-data-grid";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { basicDialogSelector, confirmDialogSelector, paginationModelSelector } from "../../../store/helpers/helperSelectors";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import { Sheet } from "../../ui/sheet";
import { Loader2 } from "lucide-react";
import { IFilter } from "../Filter/types";
import RemoveButton from "../Buttons/RemoveButton";
import AssociationFilter from "./AssociationFilter";
import Association from "./Association";
import { searchKeywordSelector } from "../../../store/search/selector";
import { initialState } from "../../../store/helpers/helperInitialState";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { errorMessage } from "../../../utils/helpers";
import { useAssociationMutations, createPayload, useLoadData, useUrlParameter, IAssociationAddProps } from "./AssociationHelper";
import { updateKeyword, resetKeyword } from "../../../store/search/reducer";
import ActiveFilterChips from "../Filter/ActiveFilterChips";
import { setOpenDeviceDrawer, setDrawerCounts, setSelectedDevice, setSelectedDeviceAlarms, setSelectedDeviceAlarmCount } from "../../../store/api/devices/reducers";
import { useGetDrawerCountsMutation, useGetAlarmsByDeviceKeyMutation } from "../../../store/api/devices/deviceApiSlice";
import { useGetDeviceFullDetailMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { setIsLoadingStatus } from "../../../store/helpers/helperReducers";
import { isOpenDeviceDrawerSelector } from "../../../store/api/devices/selectors";

interface IAssociationControlProps extends IAssociationAddProps {
  entity?: any,
  associationPermission?: boolean;
  parentEntityName?: string;
  childEntityName?: string;
  listFilters?: IFilter;
  listColumns?: any[];
  listSorting?: string;
  listResetFilterHandler: (state: any) => void;
  listCheckboxSelection?: boolean;
  listDisableColumnMenu?: boolean;
  listDisableRowSelectionOnClick?: boolean;
  listMutationKey?: any;
  listMutationPayloadKey?: any[];
  urlListParameterKey?: string;
  loadListDataKey?: string;
  listDialogOpener?: string;
  addModelHeaderName?: string;
  visibleAdd?: boolean;
  addButtonName?: string;
  showRemoveAction?: boolean;
  confirmRemoveDialogOpener?: string;
  removeConfirmationMessage?: string;
  handleRowClicked?: GridEventListener<"rowClick">;
  listDrawerItem?: React.ElementType;
  selectedItem?: any;
  onAssociationChanged?: () => void;
  listHeight?: string | number;
  disableAddButton?: boolean;
}
const AssociationControl: React.FC<IAssociationControlProps> = ({
  entity,
  associationPermission = true,
  parentEntityName,
  childEntityName,
  listFilters,
  listColumns = [],
  listSorting,
  listResetFilterHandler,
  listCheckboxSelection = true,
  listDisableColumnMenu = true,
  listDisableRowSelectionOnClick = true,
  listMutationKey,
  listMutationPayloadKey = [],
  urlListParameterKey,
  loadListDataKey,
  listDialogOpener,
  addModelHeaderName = "",
  visibleAdd = true,
  addButtonName = "Add",
  showRemoveAction,
  confirmRemoveDialogOpener = "",
  removeConfirmationMessage = "",
  handleRowClicked,
  listDrawerItem: DynamicComponent,
  selectedItem,
  onAssociationChanged,
  listHeight = "390px",
  disableAddButton = false,
  addFilters,
  addColumns,
  addSorting,
  addResetFilterHandler,
  showAddAction,
  showAddAll = true,
  addCheckboxSelection = true,
  addDisableColumnMenu = true,
  addDisableRowSelectionOnClick = true,
  addMutationKey,
  addMutationPayloadKey,
  urlAddParameterKey,
  loadAddDataKey,
  loadAddAllDataKey,
  addShowInfoMessage = false,
  addInfoMessage = "",
  addRowSelectable,
  addSetRowClass,
  disabledAddActionButton,
  sxAdd,
  isShowRemovePanelNotification=false,
  notificationMessageforRemove = ""

}) => {
  const associationRef = React.useRef<any>(null);
  const [selectedAddRows, setSelectedAddRows] = useState<GridRowId[]>([]);
  const [addLoader, setAddLoader] = useState(false);

  const [selectedRowIds, setSelectedRowIds] = useState<GridRowId[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<GridRowId>();
  const [isSingleRecordSelected, setIsSingleRecordSelected] = useState(false);
  const [listDisassociationLoader, setListDisassociationLoader] = useState(false);
  const { executeMutation } = useAssociationMutations();
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const dispatch = useStoreDispatch();
  const isDeviceDrawerOpen = useSelector(isOpenDeviceDrawerSelector);

  const [selectedDrawerItem, setSelectedDrawerItem] = useState<any>(null);
  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();

  const openDeviceDrawerHandler = async (params: any) => {
    const row = params?.row;
    if (!row) return;

    let objectKey = row.serialNumber || row.hostname || row.objectKey;
    if (objectKey && objectKey.includes(':')) {
      objectKey = objectKey.split(':')[0];
    }

    if (!objectKey) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "No device identifier found" }));
      return;
    }

    setSelectedDrawerItem(row);
    dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: true, isLoadingAlarms: true }));
    dispatch(setOpenDeviceDrawer(true));

    try {
      const args = { filters: `key=objectKey&value=${objectKey}&weightType=FULL`, objectKey };
      const [drawerCountsResponse, deviceDetailResponse, alarmResponse] = await Promise.all([
        getDrawerCounts({ deviceKey: objectKey, filters: "" }),
        getDeviceFullDetail(args),
        getAlarmsByDeviceKey({ deviceKey: objectKey, filters: "?sort=retention%2Cdesc&page=0&size=50" }),
      ]);

      if ("error" in drawerCountsResponse || "error" in deviceDetailResponse || "error" in alarmResponse) {
        let error: any = "";
        if ("error" in drawerCountsResponse) error = drawerCountsResponse.error;
        if ("error" in deviceDetailResponse) error = deviceDetailResponse.error;
        if ("error" in alarmResponse) error = alarmResponse.error;
        throw error;
      }

      const { data: configCount } = drawerCountsResponse;
      const { data: deviceData } = deviceDetailResponse;
      const { data: alarmData } = alarmResponse;

      if (!!configCount && !!deviceData && deviceData.data[0] && alarmData) {
        dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
        dispatch(setDrawerCounts(configCount));
        dispatch(setSelectedDevice({ ...deviceData.data[0], ...deviceData?.deviceParams }));
        dispatch(setSelectedDeviceAlarms(alarmData.data));
        dispatch(setSelectedDeviceAlarmCount(alarmData.totalItems || 0));
      }
    } catch (error: any) {
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
      dispatch(setOpenDeviceDrawer(false));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  // Clear selection when entity changes (e.g., switching labels)
  useEffect(() => {
    setSelectedRowIds([]);
    setIsSingleRecordSelected(false);
  }, [entity?.value]);
  const onRowSelectedHandler = (rowIds: GridRowId[]) => {
    setSelectedRowIds(rowIds);
    setIsSingleRecordSelected(false);
  };
  const [isAssociatedAdd, setIsAssociatedAdd] = useState(false);

  const clearSelectedRowIds = () => {
    setSelectedRowIds([]);
    setIsSingleRecordSelected(false);
  };

  const actionColumn = (): GridColDef => ({
    field: "actions",
    type: "actions",
    width: 100,
    cellClassName: "actions",
    getActions: (row) => {
      return [
        <RemoveButton
          key={`remove-${row.id}`}
          id={row.id} isLoading={listDisassociationLoader}
          removeHandler={(ids) => {
            dispatch(setConfirmDialog(confirmRemoveDialogOpener));
            setIsSingleRecordSelected(true);
            setSelectedRowId(ids);
          }} />,
      ];
    },
  });

  const [rowClicked, setRowClicked] = useState<boolean>(false);
  const rowClickedHandler: GridEventListener<"rowClick"> = (params, event, details) => {
    setRowClicked(true);
    if (handleRowClicked) {
      handleRowClicked?.(params, event, details);
    } else {
      openDeviceDrawerHandler(params);
    }
  };

  const columnList = React.useMemo<GridColDef[]>(() => {
    if (showRemoveAction && associationPermission)
      return [...listColumns, actionColumn()];
    else
      return listColumns;
  }, []);

  const handleRemoveAction = (actionName: string) => {
    if (actionName === "yes") {
      let idsToDelete: any[] = [];
      if (isSingleRecordSelected) {
        idsToDelete = selectedRowId ? [selectedRowId] : [];
      }
      else {
        idsToDelete = selectedRowIds;
      }

      if (entity.enabled) {
        if (rowCount > idsToDelete.length) {
          removeAssociationHandler?.(idsToDelete);
          clearSelectedRowIds();
        }
        else {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "error",
              message: "Device(s) can not be removed, since this profile is enabled and  At least one device association is required.",
            })
          );
        }
      }
      else {
        removeAssociationHandler?.(idsToDelete);
        clearSelectedRowIds();
      }
    }
  };

  const closeDialogHandler = () => {
    dispatch(setBasicDialogOpenFor(undefined));
    setSelectedRowIds([]);
    setIsSingleRecordSelected(false);
    dispatch(updateKeyword(""));
  };

  const addPopupHandler = () => {
    dispatch(setBasicDialogOpenFor(listDialogOpener));
    dispatch(updateKeyword(""));
  };

  const getSortBy = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      return Object.values(sortModel[0]).toString() as string;
    } else return "";
  };

  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const paginationModel = useSelector(paginationModelSelector);
   const paginationKeyBase = React.useMemo(() => {
    const rawKey = [
      listDialogOpener,
      loadListDataKey,
      urlListParameterKey,
      parentEntityName,
      childEntityName,
    ]
      .filter(Boolean)
      .join("-") || "AssociationControl";

    return rawKey.replace(/[^a-zA-Z0-9_-]/g, "_");
  }, [listDialogOpener, loadListDataKey, urlListParameterKey, parentEntityName, childEntityName]);

  const mappedTableId = `${paginationKeyBase}-MappedData`;
  const unMappedTableId = `${paginationKeyBase}-UnMappedData`;
  const listData = paginationModel?.[mappedTableId] || initialState.pagination;

  const filters = useUrlParameter(urlListParameterKey ?? "");
  const keyword = useSelector(searchKeywordSelector);
  const [sortOrder, setSortOrder] = useState(listSorting ?? "");

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllFilters = useCallback(() => {
    if (listResetFilterHandler) {
      dispatch(listResetFilterHandler({}) as any);
    } else {
      listFilters?.selectFilters?.forEach((filter) => {
        if (filter.id === "geoRegion" || filter.id === "addDeviceGeoRegion") return;
        dispatch(filter.setValue(filter.multiple ? [] : ""));
      });
    }
    dispatch(resetKeyword());
  }, [dispatch, listFilters?.selectFilters, listResetFilterHandler]);

  if (listSorting != null) {
    filters.set("sort", sortOrder);
  }

  filters.set("page", listData.page.toString());
  filters.set("size", listData.pageSize.toString());
  filters.set("search", keyword);
  const args = { filters: filters.toString(), id: entity?.value };

  const { data, isLoading: isListLoading, refetch: refetchMapData } = useLoadData(loadListDataKey ?? "", args);

  let rowsData: any[] = [];
  let rowCount = 0;
  rowsData = ((data as any)?.data ?? (data as any)?.content) || [];
  rowsData = rowsData.map((row: any) => {
    if (row?.siteGroupId === "999999999" || row?.siteGroupId === "999999998") {
      return {
        ...row,
        siteGroupName: "",
      };
    }
    return row;
  });
  rowCount = (data as any)?.totalItems || (data as any)?.totalElements || 0;

  useEffect(() => {
    refetchMapData();
    if (isAssociatedAdd) {
      onAssociationChanged?.();
    }
    setIsAssociatedAdd(false);
  }, [listData.page, listData.pageSize, args.filters, refetchMapData, isAssociatedAdd, onAssociationChanged, keyword]);

  const removeAssociationHandler = async (selectedRowIds: GridRowId[]) => {
    if (selectedRowIds.length <= 0)
      return;

    try {
      setListDisassociationLoader(true);
      const response = await executeMutation(
        listMutationKey,
        createPayload(
          listMutationPayloadKey,
          [
            entity?.value,
            selectedRowIds.map(id => id.toString()),
          ]
        )
      );

      if ('data' in response && response.data?.status === 200) {
        setSelectedRowIds([]);
        setListDisassociationLoader(false);
        if (response.data) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `${childEntityName}${setSelectedRowIds.length > 1 ? 's have' : ' has'} been removed from ${parentEntityName}: ${entity?.text}.`,
            })
          );
        }
      }
      else if ('error' in response && response?.error)
        throw response?.error;
    }
    catch (error) {
      setListDisassociationLoader(false);
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
    setSelectedRowIds([]);
    refetchMapData();
    onAssociationChanged?.();
  };

  const confirmDialogSx: SxProps = {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.1) !important",
      backdropFilter: "blur(2px)",
    },
    "& .MuiDialog-paper": {
      boxShadow: "none !important",
      border: "1px solid var(--border)",
    },
  };

  const sheetFooter = (
    <div className="flex items-end justify-end w-full mr-4">
      <div className="flex items-end gap-2">
        <Button variant="outline" onClick={() => { setSelectedAddRows([]); closeDialogHandler(); }}>
          Cancel
        </Button>
        <Button
          onClick={() => associationRef.current?.addRecords(selectedAddRows)}
          disabled={addLoader || selectedAddRows.length === 0}
        >
          {addLoader && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {selectedAddRows.length === 0
            ? "Add"
            : selectedAddRows.length === 1
              ? `Add 1 ${childEntityName}`
              : `Add ${selectedAddRows.length} ${childEntityName}s`}
        </Button>
        {showAddAll && (
          <Button
            disabled={addLoader}
            onClick={() => associationRef.current?.addRecords([], true)}
          >
            {addLoader && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add All
          </Button>
        )}
      </div>
    </div>
  );




  return (
    <>
      {rowClicked && DynamicComponent && <DynamicComponent data={selectedItem || selectedDrawerItem} closedButtonClicked={() => setRowClicked(false)}> </DynamicComponent>}
      {basicDialogOpenFor === listDialogOpener && (
        <Sheet
          open={true}
          onClose={() => { if (isDeviceDrawerOpen) { dispatch(setOpenDeviceDrawer(false)); } closeDialogHandler(); }}
          title={`${addModelHeaderName}${entity?.text ? ` to ${entity.text}` : ''}`}
          side="right"
          width="585px"
          footer={sheetFooter}
        >
          <Association
            ref={associationRef}
            selectedRowIds={selectedAddRows}
            setSelectedRowIds={setSelectedAddRows}
            addAssociationLoader={addLoader}
            setAddAssociationLoader={setAddLoader}
            entity={entity}
            parentEntityName={parentEntityName}
            childEntityName={childEntityName}
            filters={addFilters}
            columns={addColumns}
            addSorting={addSorting}
            resetFiltersHandler={addResetFilterHandler}
            showAddAction={showAddAction}
            showAddAll={showAddAll}
            checkboxSelection={addCheckboxSelection}
            disableColumnMenu={addDisableColumnMenu}
            disableRowSelectionOnClick={addDisableRowSelectionOnClick}
            addMutationKey={addMutationKey}
            addMutationPayloadKey={addMutationPayloadKey}
            urlAddParameterKey={urlAddParameterKey}
            loadAddDataKey={loadAddDataKey}
            loadAddAllDataKey={loadAddAllDataKey}
            showInfoMessage={addShowInfoMessage}
            infoMessage={addInfoMessage}
            rowSelectable={addRowSelectable}
            setRowClass={addSetRowClass}
            disabledActionButton={disabledAddActionButton}
            sx={sxAdd}
            setIsAssociatedAdded={(status) => {
              setIsAssociatedAdd(status);
              if (status) {
                onAssociationChanged?.();
              }
            }}
            onRowClick={rowClickedHandler}
            paginationTableId={unMappedTableId}
          />
        </Sheet>
      )}
      <div className="w-full">
        {openConfirmDialog === confirmRemoveDialogOpener && (
          <ConfirmDialog
            actionName="Yes"
            handleClickAction={(actionName) => handleRemoveAction(actionName)}
            sx={confirmDialogSx}
          >
            {removeConfirmationMessage} <strong> {entity?.text} </strong> ?
          </ConfirmDialog>
        )}
      </div>
      {isShowRemovePanelNotification && (
        <div className="flex items-center gap-2 text-sm italic text-link">      
          <span className="mt-2 mb-4 ml-1 text-sm text-muted-foreground">{notificationMessageforRemove}</span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-4 w-full">
        <div>
          <AssociationFilter
            searchTooltip={`Search by '${childEntityName}' fields for quick access.`}
            selectFilters={listFilters?.selectFilters}
            dateFilters={listFilters?.dateFilters}
            toggleButtonFilters={listFilters?.toggleButtonFilters}
            resetFilters={listResetFilterHandler}
            searchPlaceholder={`Search ${childEntityName?.toLowerCase()}s...`}
          />
        </div>
        {visibleAdd && (
          <div className="ml-auto">
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              variant="outline"
              onClick={addPopupHandler}
              disabled={!associationPermission || disableAddButton}
            >
              <Icon name="add" size={18} />
              {addButtonName}
            </Button>
          </div>)}
      </div>
      {selectedRowIds.length > 0 && (
        <div className="w-full ml-0 mt-3 mb-3">
          <div className="flex flex-row items-center gap-2">
            <span className="text-sm text-muted-foreground ">
               {selectedRowIds.length} {selectedRowIds.length === 1 ? `${childEntityName?.toLowerCase()}` : `${childEntityName?.toLowerCase()}s`} selected
            </span>

            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-link bg-background hover:text-link-hover"
              onClick={clearSelectedRowIds}
            >
              Clear
            </Button>
             <Button
              className="h-10 px-2 gap-1 text-left align-middle font-medium text-accent-foreground border border-border bg-muted shadow-md hover:bg-accent hover:text-accent-foreground"
              variant="secondary"
              size="default"
              onClick={() => dispatch(setConfirmDialog(confirmRemoveDialogOpener))}
              disabled={listDisassociationLoader || !associationPermission}
            >
              <Icon name="delete" size={16} />
              Remove
            </Button>
          </div>
        </div>
      )}
      <div className="flex justify-between items-end mb-0 mt-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mb-1.5">{`${rowsData?.length || 0} results`}</span>
          <ActiveFilterChips
            filters={listFilters?.selectFilters || []}
            searchKeyword={keyword}
            onClearSearch={handleClearSearch}
            onClearAll={handleClearAllFilters}
            dispatch={dispatch}
          />
        </div>
      </div>

      <div className="min-w-full">
        <CustomDataGrid
          tableId={mappedTableId}
          rows={rowsData}
          rowCount={rowCount}
          columns={columnList}
          loading={isListLoading}
          checkboxSelection={listCheckboxSelection}
          disableColumnMenu={listDisableColumnMenu}
          disableRowSelectionOnClick={listDisableRowSelectionOnClick}
          onRowClick={rowClickedHandler}
          onRowSelectionModelChange={onRowSelectedHandler}
          rowSelectionModel={selectedRowIds}
          keepNonExistentRowsSelected={true}
          getRowId={(row: any) => row.objectKey ?? row.id}
          onSortModelChange={(params: GridSortModel) => setSortOrder?.(getSortBy(params))}
          sx={{ '& .MuiDataGrid-selectedRowCount': { display: 'none' }, '& .MuiDataGrid-footerContainer': { justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }, '& .MuiDataGrid-scrollbar--horizontal': { display: 'none' } }}
          fillContainer
        />
      </div>
    </>
  );
};
export default AssociationControl;